import {observer} from 'mobx-react'
import {runInAction} from 'mobx'
import {t} from 'ttag'
import autobind from 'autobind-decorator'
import * as React from 'react'
import Moment from 'moment'
import {Button, Form, FormGroup, Input, Label, Alert} from 'reactstrap'
import {FlightFormState, getStateInstance} from './state'
import {FieldState} from 'formstate'
import AirlineAutocomplete, {Airline} from '../Forms/AirlineAutocomplete'
import {FieldFeedBack} from '../Forms/FieldFeedBack'
import {DatePicker, Localization} from 'react-widgets'
import {navigate} from '../../util/navigate'
import {getLanguageCode} from '../../locales'

import {default as MomentLocalizer} from 'react-widgets-moment'
import { RadioButtonOption, RadioButtons } from '../RadioButtons/RadioButtons'
Moment.updateLocale('en', {week : {dow : 1, doy : 4}});  // Start week on Monday
Moment.locale('en')

const momentLocalizer = new MomentLocalizer(Moment)

export enum FlightIssue {
    CANCELLED = 'FlightIssue.CANCELLED',
    BOARDING_DENIED = 'FlightIssue.BOARDING_DENIED',
    LATE_DEPARTURE = 'FlightIssue.LATE_DEPARTURE',
    LATE_ARRIVAL = 'FlightIssue.LATE_ARRIVAL',
}

const FLIGHT_ISSUE_CHOICES: RadioButtonOption[] = [
    { text: t`Flight was cancelled`, value: FlightIssue.CANCELLED },
    { text: t`Boarding was denied`, value: FlightIssue.BOARDING_DENIED },
    { text: t`Flight departed more than 3 hours late`, value: FlightIssue.LATE_DEPARTURE },
    { text: t`Flight arrived more than 3 hours late`, value: FlightIssue.LATE_ARRIVAL },
]

export enum StatedCause {
    NO_EXPLANATION = '-1',
    PREVIOUS_DELAY = 'StatedCause.PREVIOUS_DELAY',
    TECHNICAL_ISSUE = 'StatedCause.TECHNICAL_ISSUE',
    AIRPORT_OPERATIONS = 'StatedCause.AIRPORT_OPERATIONS',
    WEATHER = 'StatedCause.WEATHER',
    STRIKE = 'StatedCause.STRIKE',
    OTHER = 'StatedCause.OTHER',
}

const STATED_REASON_CHOICES: RadioButtonOption[] = [
    { text: t`No explanation was given / don't remember`, value: StatedCause.NO_EXPLANATION },
    { text: t`The plane was delayed from a previous flight`, value: StatedCause.PREVIOUS_DELAY },
    { text: t`There was a technical issue with the plane`, value: StatedCause.TECHNICAL_ISSUE },
    { text: t`The issue was caused by airport operations`, value: StatedCause.AIRPORT_OPERATIONS },
    { text: t`Bad weather`, value: StatedCause.WEATHER },
    { text: t`Strike`, value: StatedCause.STRIKE },
    { text: t`Other`, value: StatedCause.OTHER },
]

export enum FlightCancellationNotification {
    LESS_TWO_WEEKS = 'FlightCancellationNotification.LESS_TWO_WEEKS',
    MORE_TWO_WEEKS = 'FlightCancellationNotification.MORE_TWO_WEEKS',
    NOT_INFORMED = 'FlightCancellationNotification.NOT_INFORMED',
}

const FLIGHT_CANCELLATION_NOTIFICATION_CHOICES: RadioButtonOption[] = [
    { text: t`Less than 14 days`, value: FlightCancellationNotification.LESS_TWO_WEEKS },
    { text: t`More than 14 days`, value: FlightCancellationNotification.MORE_TWO_WEEKS },
    { text: t`Wasn't informed`, value: FlightCancellationNotification.NOT_INFORMED },
]

interface Props {
    location: Location
}

@observer
export class FlightDetailsForm extends React.Component<Props> {
    state = {
        alertMessage: '',
    }

    render () {
        let pageState = getStateInstance()
        let fields = pageState.formState.flights.$
        let flightForms = fields.flights.$

        return <Form onSubmit={this.onSubmit}>
            <h1>{t`Flight details`}</h1>

            <p>
                {t`
                    Let's check how much compensation you can receive - please enter
                    the following details about your flight
                `}
            </p>

            {flightForms.map((f, idx) => this.renderFlightForm(f, idx, flightForms.length > 1))}

            <FormGroup className="mt-5">
                <a ref={fields.flight_issue.positionRef as any}/>
                <Label for="issue">{t`What was the issue with the flight?`}</Label>
                <RadioButtons
                    name="flight_issue"
                    options={FLIGHT_ISSUE_CHOICES}
                    onChange={this.onFlightIssueChange}
                    value={fields.flight_issue.$}
                />
                <FieldFeedBack field={fields.flight_issue}/>
            </FormGroup>

            {fields.flight_issue.$ === FlightIssue.CANCELLED && (
                <FormGroup>
                    <Label for="issue">{t`How many days before the flight were you informed?`}</Label>
                    <RadioButtons
                        name="flight_cancellation_notification"
                        options={FLIGHT_CANCELLATION_NOTIFICATION_CHOICES}
                        onChange={this.onFlightCancellationNotificationChange}
                        value={fields.flight_cancellation_notification.$}
                    />
                    <FieldFeedBack field={fields.flight_cancellation_notification}/>
                </FormGroup>
            )}

            {fields.flight_issue.$ === FlightIssue.LATE_ARRIVAL && (
                <FormGroup>
                    <Label for="issue">{t`How long was the delay in hours?`}</Label>
                    <Input
                        type="number"
                        name="arrival_delay"
                        invalid={!!fields.arrival_delay.error}
                        value={fields.arrival_delay.value || ''}
                        onChange={(e) => fields.arrival_delay.onChange(e.target.value)}
                        onBlur={() => fields.arrival_delay.validate()}
                    />
                    <FieldFeedBack field={fields.arrival_delay}/>
                </FormGroup>
            )}

            {flightForms.length > 1 ?
                <FormGroup>
                    <Label for="issue">{t`Which flight had issues?`}</Label>
                    <RadioButtons
                        name="problem_flight"
                        options={this.getProblemFlightChoices()}
                        onChange={this.onProblemFlightChange}
                        value={fields.problem_flight_position.$}
                    />
                    <FieldFeedBack field={fields.problem_flight_position}/>
                </FormGroup>
                :
                null
            }

            <FormGroup>
                <Label for="reason">{t`How did the airline explain the issue?`}</Label>
                <RadioButtons
                    name="stated_cause"
                    options={STATED_REASON_CHOICES}
                    onChange={this.onStatedCauseChange}
                    value={fields.stated_cause.$}
                />
                <FieldFeedBack field={fields.stated_cause}/>
            </FormGroup>

            {this.renderCOVID19Warning(flightForms, fields.flight_issue)}

            {!pageState.isClaimable ? 
                <Alert color="danger">
                    Compensation is not available for this flight under EU regulation 261.
                    {this.state.alertMessage}
                </Alert>
                :
                null
            }

            <Alert color="primary">
                {t`No waiting. Get an instant online`} <b>{t`compensation estimate.`}</b>
            </Alert>
            
            <Button className="submit" block color="primary">{t`Check compensation`}</Button>

        </Form>
    }

    onFlightIssueChange ({ target }: React.ChangeEvent<HTMLInputElement>) {
        let pageState = getStateInstance()
        let fields = pageState.formState.flights.$
        fields.flight_issue.onChange(target.value)
        fields.flight_issue.validate()
    }

    getProblemFlightChoices () {
        let pageState = getStateInstance()
        let fields = pageState.formState.flights.$
        let flightForms = fields.flights.$
        return flightForms.map((flight, key) => {
            return {
                text: `${flight.$.departure_airport.$.city} to ${flight.$.destination_airport.$.city}`,
                value: key,
            }
        })
    }

    onProblemFlightChange ({ target }: React.ChangeEvent<HTMLInputElement>) {
        let pageState = getStateInstance()
        let fields = pageState.formState.flights.$
        fields.problem_flight_position.onChange(Number(target.value))
        fields.problem_flight_position.validate()
    }

    onStatedCauseChange ({ target }: React.ChangeEvent<HTMLInputElement>) {
        let pageState = getStateInstance()
        let fields = pageState.formState.flights.$
        fields.stated_cause.onChange(target.value)
        fields.stated_cause.validate()
    }

    onFlightCancellationNotificationChange ({ target }: React.ChangeEvent<HTMLInputElement>) {
        let pageState = getStateInstance()
        let fields = pageState.formState.flights.$
        fields.flight_cancellation_notification.onChange(target.value)
        fields.flight_cancellation_notification.validate()
    }

    @autobind
    async onSubmit (e) {
        e.preventDefault()

        let pageState = getStateInstance()
        let fields = pageState.formState.flights.$
        let flightForms = fields.flights.$

        let validationResult = await pageState.formState.flights.validate()

        if (!validationResult.hasError) {
            let problemFlight = flightForms[fields.problem_flight_position.value]
            let isClaimable = this.isCancelationFlightClaimable(
                fields.flight_issue.value,
                problemFlight.$.departure_date.value
            )

            if (isClaimable) {
                let airline = flightForms[0].$.airline.$.id

                if (!pageState.isDirectFlight) {
                    airline = flightForms[fields.problem_flight_position.value].$.airline.$.id
                }
    
                let airports = [flightForms[0].$.departure_airport.$.id]
    
                flightForms.map((flight) => {
                    airports.push(flight.$.destination_airport.$.id)
                })
    
                let response = await fetch(
                    `/api/cases/compensation-check`,
                    {
                        method: 'POST',
                        headers: {
                            'Content-Type': 'application/json'
                        },
                        body: JSON.stringify({
                            airline: airline,
                            airports: airports,
                        }),
                    }
                )
    
                if (response.status == 200) {
                    let result = await response.json()
                    runInAction(() => {
                        pageState.isClaimable = result['can_claim']
                        pageState.amountPerPassenger = result['amount']
                    })
                }
    
                if (pageState.isClaimable) {
                    navigate('/claim-form/compensation-estimate', getLanguageCode(this.props.location))
                }
            }
        }
    }

    @autobind
    onAirlineChange (field: FieldState<Airline>, selected: Airline[]) {
        field.onChange(selected.length > 0 ? selected[0] : null)
        field.validate()
    }

    renderCOVID19Warning (flights, flightIssue) {
        let startDate = new Date('03/01/2020 00:00')
        let endDate = new Date('06/15/2020 23:59')

        if (flights[0] && 
            flights[0].$.departure_date.value >= startDate && 
            flights[0].$.departure_date.value <= endDate && 
            flightIssue.value == 'FlightIssue.CANCELLED') {

            let pageState = getStateInstance()
            pageState.onShowCovid19Warning()
                
            return <Alert color="warning">
                Please be informed that due to COVID-19 quarantine, flights to or from some countries 
                are cancelled. This temporary suspension of air travel is triggered by World Health 
                Health Organisation's (WHO) recommendations jointly with International Civil Aviation 
                Organization (ICAO), so it is considered an extraordinary circumstance. As a result, 
                if your cancelled flight was scheduled to depart in the quarantine period, there are 
                high chances that you will not be entitled to compensation under Regulation (EC) 
                261/2004.
            </Alert>
        }
    }

    isCancelationFlightClaimable (
        flightIssue: string,
        problemFlightDepartureDate: Date,
    ) {
        let pageState = getStateInstance()

        if (flightIssue === FlightIssue.CANCELLED) {
            let today = new Date
            let datesDiff = problemFlightDepartureDate.getTime() - today.getTime()
            let datesDiffInDays = Math.floor(datesDiff / (1000 * 60 * 60 * 24))

            if (datesDiffInDays >= 15) {
                pageState.isClaimable = false
                this.setState({
                    alertMessage: 'The flight cancelled earlier than 14 days before departure date, so compensation is not available in this case.'
                })

                return false
            }
        }

        return true
    }

    private renderFlightForm(form: FlightFormState, idx, showHeader: boolean) {
        if (!form.$.departure_airport.$ || !form.$.destination_airport.$) return null

        let fromCity = form.$.departure_airport.$.city
        let toCity = form.$.destination_airport.$.city

        return <div key={idx}>
            {showHeader ?
                <h3 className="mt-5 mb-3">
                    {t`Flight from ${fromCity} to ${toCity}`}
                </h3>
                :
                null
            }

            <FormGroup>
                <a ref={form.$.airline.positionRef as any}/>
                <Label for="airline">{t`Airline`}</Label>
                <AirlineAutocomplete
                    id="airline-autocomplete"
                    name="airline"
                    selected={form.$.airline.value}
                    onChange={(sel) => this.onAirlineChange(form.$.airline, sel)}
                    onBlur={form.$.airline.validate}
                />
                <FieldFeedBack field={form.$.airline}/>
            </FormGroup>

            <FormGroup>
                <a ref={form.$.flight_number.positionRef as any}/>
                <Label for="flight-number">{t`Flight number`}</Label>
                <Input
                    type="text"
                    name="flight-number"
                    invalid={!!form.$.flight_number.error}
                    value={form.$.flight_number.value || ''}
                    onChange={(e) => form.$.flight_number.onChange(e.target.value)}
                    onBlur={form.$.flight_number.validate}
                />
                <FieldFeedBack field={form.$.flight_number}/>
            </FormGroup>

            <FormGroup>
                <a ref={form.$.departure_date.positionRef as any}/>
                <Label for="departure-date">{t`Scheduled departure date`}</Label>
                <Localization date={momentLocalizer}>
                    <DatePicker
                        name="departure-date"
                        valueDisplayFormat="DD.MM.YYYY"
                        valueEditFormat="DD.MM.YYYY"
                        value={form.$.departure_date.value}
                        onChange={
                            (value) => {form.$.departure_date.onChange(value); form.$.departure_date.validate()}
                        }
                        onBlur={() => form.$.departure_date.validate()}
                    />
                </Localization>
                <FieldFeedBack field={form.$.departure_date}/>
            </FormGroup>
        </div>
    }
}
